"use client"

// Vuelta del pago en useredy.com a la página servida en un «Dominio propio»
// (lib/microsite/hostedPayment.ts). Lee el fragmento con el que llega el cliente:
//   - #pago=<token>: sondea /intent-status hasta que la orden existe (la crea
//     /complete o el propio sondeo desde el intent) y abre la confirmación;
//   - #pago-cancelado: le devuelve el carrito que llevaba.
// En ambos casos limpia el fragmento y el stash, para que un refresh no repita.
import { useEffect, useRef, useState } from "react"
import { useTranslations } from "next-intl"
import { parseWelcomeCoupon } from "@/lib/marketing/welcomeCouponWire"
import type { CartItem } from "./types"
import type { ConfirmedOrder } from "./sheet/OrderSheet"
import {
  parsePaymentReturn,
  readHandoffStash,
  clearHandoffStash,
  confirmedOrderFromIntent,
  type IntentStatusResponse,
  type PaymentHandoffStash,
} from "@/lib/microsite/hostedPayment"

const POLL_MS = 2_000
/** /complete crea la orden ~1 s después del cobro; el cron es el respaldo (~1 min). */
const GIVE_UP_MS = 90_000

export function usePaymentReturn(
  slug: string,
  handlers: { restoreCart: (cart: CartItem[]) => void; showConfirmation: (order: ConfirmedOrder, stash: PaymentHandoffStash | null) => void },
): { notice: string | null } {
  const t = useTranslations("public.onlineOrder.checkout")
  const [notice, setNotice] = useState<string | null>(null)
  // El fragmento se lee UNA vez y se guarda: el efecto lo borra de la URL, y
  // en dev StrictMode monta dos veces — el segundo montaje (el que queda vivo)
  // tiene que seguir sabiendo a qué volvió el cliente.
  const returnRef = useRef<ReturnType<typeof parsePaymentReturn> | undefined>(undefined)
  const handlersRef = useRef(handlers)
  useEffect(() => {
    handlersRef.current = handlers
  })

  useEffect(() => {
    if (returnRef.current === undefined) returnRef.current = parsePaymentReturn(window.location.hash)
    const ret = returnRef.current
    if (!ret) return
    const clearHash = () =>
      window.history.replaceState(null, "", window.location.pathname + window.location.search)

    if (ret.kind === "cancelled") {
      const stash = readHandoffStash(slug, null)
      clearHash()
      clearHandoffStash()
      if (stash && stash.cart.length > 0) handlersRef.current.restoreCart(stash.cart)
      return
    }

    const token = ret.token
    const stash = readHandoffStash(slug, token)
    clearHash()
    setNotice(t("returnConfirming"))

    let stopped = false
    const started = Date.now()
    const tick = async () => {
      if (stopped) return
      try {
        const res = await fetch(`/api/online-orders/intent-status?token=${encodeURIComponent(token)}`)
        if (res.ok) {
          const data = (await res.json()) as IntentStatusResponse
          const order = confirmedOrderFromIntent(token, data, stash, parseWelcomeCoupon(data))
          if (order && !stopped) {
            stopped = true
            clearHandoffStash()
            setNotice(null)
            handlersRef.current.showConfirmation(order, stash)
            return
          }
        }
      } catch {
        /* transitorio: se reintenta en el próximo tick */
      }
      if (Date.now() - started > GIVE_UP_MS) {
        stopped = true
        setNotice(t("returnSlow"))
        return
      }
      setTimeout(tick, POLL_MS)
    }
    void tick()
    return () => {
      stopped = true
    }
    // Solo al montar: el fragmento es de esta visita.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { notice }
}
